/* ────────────────────────────────────────────────────────────────
   RS Swatch Return — scroll restore + success reopen

   The free-swatch modal submits a native {% form 'contact' %}, which
   returns to the ORIGIN path. rs-form-validate strips the
   ?contact_posted param on load, so detection MUST key on the rendered
   DOM element, not the URL. The modal's success branch is in the DOM
   but the <dialog> is closed, so without this the customer sees nothing.

   BEFORE SUBMIT: on the swatch form's submit, gate on
   form.checkValidity() so we NEVER stash on a validation-blocked
   submit (independent of listener ordering). Stash { scrollY, ts }.

   ON LOAD: if #rs-swatch-modal [data-rs-form-success] exists (SCOPED —
   never the bare attribute, so contact-hero's success element can't
   trigger this), restore scroll from a fresh (<5min) stash and reopen
   the modal via the public rs-modal API. Stale/missing stash → do NOT
   scroll (never jump to 0).

   Surface-agnostic beyond the #rs-swatch-modal id — works on every page
   that renders the modal. Self-loaded (defer) AFTER rs-modal.js in DOM
   order, so window.rsModal is defined by the time this runs.

   ── THIS FILE WRITES localStorage['rs_swatch_prompt'] ────────────
   It previously owned no persisted state beyond its own sessionStorage
   stash. It now also marks the swatch as submitted, which closes the gap
   where a swatch requested from a PDP (or any non-homepage surface) was
   never recorded and the homepage popup kept firing every 24 hours.

   SHAPE — { submitted, shownAt }, and it is SHARED:

     submitted  permanent, never expires, no clearing path. Set by BOTH
                this file and assets/rs-swatch-prompt.js.
     shownAt    ms timestamp of the last time the popup opened. Belongs
                to the PROMPT. This file NEVER sets it and must preserve
                whatever is already stored — including the legacy `ts`
                key, which rs-swatch-prompt.js still reads as shownAt. A
                naive write that dropped it would zero a legacy visitor's
                last-shown time.

   The two writers are idempotent: both do a read-modify-write and both
   serialise exactly these two keys, so a double write on the homepage
   (where both files load) produces identical bytes.

   ⚠ IF YOU CHANGE THE SWATCH STATE SHAPE, BOTH FILES MUST CHANGE —
   this one and assets/rs-swatch-prompt.js. Neither can detect the
   other's divergence: a mismatched key name just writes to a fresh slot
   and suppression silently stops working. The key is registered in
   CLAUDE.md's Cross-File Selector Registry for that reason.

   ── WHY THE WRITE SITS BEHIND THE AMBIGUITY BAIL ─────────────────
   Shopify's ?contact_posted=true is page-scoped, not form-scoped, so
   [data-rs-form-success] inside this modal is NOT by itself proof the
   SWATCH form posted — see the AMBIGUITY GUARD comment below. Because
   `submitted` is permanent and has no clearing path, a false positive
   silently kills the popup for that visitor forever. So the write goes
   AFTER that bail, where the success signal is attributable to the
   swatch form. DO NOT move it earlier for "reliability" — the cost of
   recording one submit too many is far higher than missing one.
   ──────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window.rsSwatchReturnLoaded) return; // window-level double-load guard
  window.rsSwatchReturnLoaded = true;

  var MODAL_ID = 'rs-swatch-modal';
  var STORAGE_KEY = 'rs:swatch-return';
  var MAX_AGE_MS = 5 * 60 * 1000; // 5 minutes

  /* localStorage key OWNED by assets/rs-swatch-prompt.js (its STORE_KEY).
     This file is a SECOND WRITER of the same key — see the shape contract
     in the header. The literal is duplicated across the two files by
     necessity (no shared helper), so renaming it in one and not the other
     writes to a fresh slot and silently stops suppressing the popup. */
  var PROMPT_STORE_KEY = 'rs_swatch_prompt';

  function bindStash(form) {
    form.addEventListener('submit', function () {
      // Constraint-validity gate: false when any [required] field is
      // invalid, so a validation-blocked submit never stashes — holds
      // regardless of listener order vs the validator. `novalidate`
      // suppresses only the native UI; checkValidity() still works.
      if (!form.checkValidity()) return;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify({
          scrollY: window.scrollY || window.pageYOffset || 0,
          ts: Date.now()
        }));
      } catch (e) { /* private mode / quota — non-fatal, skip stash */ }
    });
  }

  /* Record the swatch as submitted, permanently suppressing the timed
     homepage popup (assets/rs-swatch-prompt.js reads this).

     READ-MODIFY-WRITE, deliberately. shownAt belongs to the prompt, so it
     is read back and re-persisted untouched — `s.ts` included, which is
     the legacy shared timestamp that file still accepts as shownAt
     (rs-swatch-prompt.js readState). Dropping it would zero an existing
     visitor's last-shown time.

     Writes ONLY the two current keys, mirroring that file's writeState,
     so any legacy keys drop off on this write rather than being carried
     forward. Re-writing an already-submitted record produces identical
     bytes, so there is no skip branch — the writes are idempotent by
     construction and one of them being redundant is the point. */
  function markSubmitted() {
    try {
      var s = {};
      var raw = localStorage.getItem(PROMPT_STORE_KEY);
      var parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') s = parsed;

      localStorage.setItem(PROMPT_STORE_KEY, JSON.stringify({
        submitted: true,
        shownAt: s.shownAt || s.ts || 0 // s.ts is the legacy shared timestamp
      }));
    } catch (e) { /* private mode / quota / malformed JSON — non-fatal */ }
  }

  function restoreAndOpen() {
    var success = document.querySelector('#' + MODAL_ID + ' [data-rs-form-success]');
    if (!success) return; // not a return-from-success load — do nothing

    var raw = null;
    try { raw = sessionStorage.getItem(STORAGE_KEY); } catch (e) { raw = null; }

    if (raw) {
      var data = null;
      try { data = JSON.parse(raw); } catch (e) { data = null; }
      if (data &&
          typeof data.scrollY === 'number' &&
          typeof data.ts === 'number' &&
          (Date.now() - data.ts) < MAX_AGE_MS) {
        var reduce = !!(window.matchMedia &&
          window.matchMedia('(prefers-reduced-motion: reduce)').matches);
        try {
          window.scrollTo({ top: data.scrollY, left: 0, behavior: reduce ? 'auto' : 'smooth' });
        } catch (e) {
          window.scrollTo(0, data.scrollY); // older browsers: no options object
        }
      }
      // stale or malformed → intentionally no scroll (never jump to 0)
    }

    // Clear the key once we're on the success load, whatever its state.
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }

    /* AMBIGUITY GUARD — 2026-08-06.
       Shopify's ?contact_posted=true is page-scoped, not
       form-scoped: every {% form 'contact' %} on the page
       renders posted_successfully? true after ANY contact
       submit. So [data-rs-form-success] inside this modal
       is NOT proof the swatch form was the one submitted.
       When a page carries more than one form_type='contact'
       form (today: /pages/contact, which has
       rs-contact-hero's form plus this modal's SwatchForm),
       attribution is impossible and auto-opening would fire
       on an unrelated submit. Bail instead.
       Cost: a swatch submitted FROM such a page shows no
       confirmation modal. Accepted.
       Real fix: move the swatch off form_type 'contact'
       (e.g. 'customer', separate ?customer_posted param) or
       go AJAX. Then delete this guard. */
    if (document.querySelectorAll('form input[name="form_type"][value="contact"]').length > 1) {
      return;
    }

    /* Past the ambiguity bail, so this success IS attributable to the
       swatch form — only now is it safe to write the permanent flag.
       See "WHY THE WRITE SITS BEHIND THE AMBIGUITY BAIL" in the header;
       do not hoist this above the bail. */
    markSubmitted();

    // Reopen so the customer sees the confirmation.
    if (window.rsModal && typeof window.rsModal.open === 'function') {
      window.rsModal.open(MODAL_ID);
    }
  }

  function init() {
    var form = document.querySelector('#' + MODAL_ID + ' form');
    if (form) bindStash(form);
    restoreAndOpen();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
